import { useEffect, useRef } from 'react';
import { Clock, UserX, CalendarX, CalendarClock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FeedbackCard from '../common/FeedbackCard';
import { useClassLogs } from '../../hooks/useClassLogs';
import { useSelection } from '../../context/SelectionContext';
import { AttendanceIndicator, HomeworkIndicator, HomeworkAssignedIndicator, VideoIndicator } from './StatusIndicator';

// AI evals cutoff date: January 19, 2026
// Modules before this date use old eval format (teachers not trained on AI-friendly writing)
// So we permanently hide AI evals for modules before this date on Parent Portal
const AI_EVALS_CUTOFF_DATE = { year: 2026, month: 1, day: 19 };

// Check if a module date is before the AI evals cutoff
function isModuleBeforeCutoff(eventDate) {
  if (!eventDate) return false;

  const [yearStr, monthStr, dayStr] = eventDate.split('-');
  const moduleYear = parseInt(yearStr, 10);
  const moduleMonth = parseInt(monthStr, 10);
  const moduleDay = parseInt(dayStr, 10);

  if (moduleYear < AI_EVALS_CUTOFF_DATE.year) return true;
  if (moduleYear > AI_EVALS_CUTOFF_DATE.year) return false;
  if (moduleMonth < AI_EVALS_CUTOFF_DATE.month) return true;
  if (moduleMonth > AI_EVALS_CUTOFF_DATE.month) return false;
  return moduleDay < AI_EVALS_CUTOFF_DATE.day;
}

export default function ModuleDropdown({ eventId, eventDate, isOpen, attendanceStatus, homeworkStatus, videoWatched, isCanceled }) {
  const { t } = useTranslation();
  const { selectedChild } = useSelection();
  const { fetchClassLogs, getLogs } = useClassLogs(selectedChild?.id);
  const hasFetchedRef = useRef(false);

  // Check if student was absent (status 4) or class was canceled
  const isAbsent = attendanceStatus === 4 || attendanceStatus === '4';
  const isClassCanceled = isCanceled && isCanceled > 0;

  // Permanently hide AI evals for modules before Jan 19, 2026
  const isEvalsHidden = isModuleBeforeCutoff(eventDate);

  useEffect(() => {
    // Don't fetch class logs if student was absent or class was canceled - no feedback to show
    if (isOpen && !hasFetchedRef.current && selectedChild && !isAbsent && !isClassCanceled) {
      hasFetchedRef.current = true;
      fetchClassLogs(eventId);
    }
  }, [isOpen, eventId, fetchClassLogs, selectedChild, isAbsent, isClassCanceled]);

  const logs = getLogs(eventId);

  // Simple: show loading if we should fetch and haven't got a response yet
  // logs is undefined until fetch completes, then null/object
  const loading = !isAbsent && !isClassCanceled && logs === undefined;

  // Check for published evaluation only
  const hasLogs = logs && logs.evaluation;

  const hasIndicators =
    (!isClassCanceled && attendanceStatus != null) ||
    homeworkStatus != null ||
    videoWatched != null;

  const renderContent = () => {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 px-3 py-3 sm:px-5 sm:py-4 border-t border-gray-100 dark:border-gray-700">
        {/* Status badges - always show if available, regardless of future/past */}
        {hasIndicators && (
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-gray-200 dark:border-gray-700">
            {!isClassCanceled && <AttendanceIndicator status={attendanceStatus} />}
            <HomeworkAssignedIndicator hasHomework={homeworkStatus != null} />
            <HomeworkIndicator status={homeworkStatus} />
            <VideoIndicator watched={videoWatched} />
          </div>
        )}

        {/* Teacher Feedback Section - min-height prevents wobble during accordion animation */}
        <div className="min-h-[60px] sm:min-h-[72px]">
          <h4 className="text-[10px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 sm:mb-3">{t('module.teacherFeedback')}</h4>
          {isClassCanceled ? (
            <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500">
              <CalendarX className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <p className="text-xs sm:text-sm">
                {t('module.feedbackCanceled')}
              </p>
            </div>
          ) : isAbsent ? (
            <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500">
              <UserX className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <p className="text-xs sm:text-sm">
                {t('module.feedbackAbsent')}
              </p>
            </div>
          ) : loading ? (
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              <div className="h-3 sm:h-4 w-32 sm:w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          ) : isEvalsHidden ? (
            // For modules before Jan 19, 2026, always show hidden message (regardless of whether logs exist)
            <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500">
              <CalendarClock className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <p className="text-xs sm:text-sm">
                {t('module.feedbackHiddenUntilLaunch')}
              </p>
            </div>
          ) : !hasLogs ? (
            <div className="flex items-center gap-2 text-gray-400 dark:text-gray-500">
              <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
              <p className="text-xs sm:text-sm">
                {t('module.feedbackPending')}
              </p>
            </div>
          ) : (
            <FeedbackCard
              label={t('module.classFeedback')}
              textKr={logs.evaluation}
              textEn={logs.evaluation_en}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="accordion-wrapper" data-open={isOpen}>
      <div className="accordion-content">
        {renderContent()}
      </div>
    </div>
  );
}
